import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { blogApi } from "../api/blogApi";
import { BlogPost, BlogComment, CATEGORY_META } from "../types/Blog";
import { useAuth } from "../AuthContext";
import BlogComments from "../components/BlogComments";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Code2,
  Building2,
  MessageSquare,
  BookOpen,
} from "lucide-react";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  algorithm: <Code2 className="w-3.5 h-3.5" />,
  "system-design": <Building2 className="w-3.5 h-3.5" />,
  behavioral: <MessageSquare className="w-3.5 h-3.5" />,
  general: <BookOpen className="w-3.5 h-3.5" />,
};

const CATEGORY_COLORS: Record<string, string> = {
  algorithm: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  "system-design": "bg-blue-500/15 text-blue-400 border-blue-500/20",
  behavioral: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  general: "bg-gray-500/15 text-gray-400 border-gray-500/20",
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      setIsLoading(true);
      try {
        if (!slug) return;
        const data = await blogApi.getBySlug(slug);
        setPost(data);
        const cmts = await blogApi.getComments(data.id);
        setComments(cmts);
      } catch (error) {
        console.error("Error loading blog post:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPost();
  }, [slug]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  const estimateReadTime = (content: string) => {
    const words = content.replace(/<[^>]*>/g, "").split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-4">Post not found</p>
          <button
            onClick={() => navigate("/blog")}
            className="text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            Back to blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${post.title} - AlgoBuddy Blog`}</title>
        <meta name="description" content={post.excerpt} />
      </Helmet>

      <main className="min-h-screen bg-gray-950">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-gray-950/80 backdrop-blur-lg border-b border-gray-800/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
            <button
              onClick={() => navigate("/blog")}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Blog
            </button>
            {user && (
              <button
                onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Article */}
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
          {/* Header */}
          <header className="mb-10">
            <div className="flex items-center gap-2.5 mb-5">
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border ${CATEGORY_COLORS[post.category]}`}
              >
                {CATEGORY_ICONS[post.category]}
                {CATEGORY_META[post.category]?.label || post.category}
              </span>
              <span className="text-xs text-gray-600 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {estimateReadTime(post.content)} min read
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight mb-5">
              {post.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                {post.authorPhoto ? (
                  <img
                    src={post.authorPhoto}
                    alt={post.author}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 text-xs font-medium">
                    {post.author?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <span className="text-gray-300">{post.author}</span>
              </div>
              <span className="text-gray-700">|</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.createdAt)}
              </span>
            </div>
          </header>

          {/* Cover image */}
          {post.coverImage && (
            <div className="mb-10">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full rounded-xl"
              />
            </div>
          )}

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-10" />

          {/* Content */}
          <div
            className="prose prose-invert prose-lg max-w-none
              prose-headings:text-white prose-headings:font-semibold
              prose-p:text-gray-300 prose-p:leading-[1.85] prose-p:font-light
              prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
              prose-code:text-emerald-400 prose-code:bg-gray-800/50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-normal
              prose-pre:bg-gray-900 prose-pre:border prose-pre:border-gray-800 prose-pre:rounded-xl
              prose-blockquote:border-emerald-500/30 prose-blockquote:text-gray-400 prose-blockquote:italic
              prose-strong:text-white
              prose-img:rounded-xl prose-img:border prose-img:border-gray-800
            "
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent my-16" />

          {/* Comments */}
          <BlogComments postId={post.id} comments={comments} setComments={setComments} />
        </article>
      </main>
    </>
  );
};

export default BlogPostPage;
